import React, { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import PersonIcon from "@mui/icons-material/Person";
import HistoryIcon from "@mui/icons-material/History";
import ViewListIcon from "@mui/icons-material/ViewList";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { useGetBriefingHistoryQuery } from "../../store/apis/briefingApi";
import { components } from "../../types/api";
import { BriefingStatusChip, CampaignStatusChip } from "../common/StatusChips";
import {
  BriefingHistoryChange,
  BriefingHistoryEntry,
  formatHistoryValue,
  historyActionLabel,
  historyActionTone,
  historyChangeRows,
  historyFieldLabel,
  historyUserName,
  isEmptyHistoryValue,
  isLineEntry,
} from "../../utils/briefingHistory";

type BriefingStatus = components["schemas"]["BriefingStatus"];
type AssociatedCampaignStatus =
  components["schemas"]["_36_Enums.AssociatedCampaignStatus"];

interface BriefingHistoryTabProps {
  briefingId: string;
}

type Scope = "all" | "briefing" | "lines";

// Filas que se ven de entrada en cada entrada; el resto, al pulsar "ver más".
const VISIBLE_ROWS = 8;
const ALL = "__all__";
const SYSTEM_USER = "__system__";

const formatDateTime = (iso: string) => dayjs(iso).format("DD/MM/YYYY HH:mm");

const ChangeValue: React.FC<{
  entry: BriefingHistoryEntry;
  field: string;
  value: unknown;
  previous?: boolean;
}> = ({ entry, field, value, previous }) => {
  const { t, i18n } = useTranslation();
  const sx = previous ? { opacity: 0.6, textDecoration: "line-through" } : {};

  if (field === "status" && !isEmptyHistoryValue(value)) {
    if (isLineEntry(entry) && !entry.action.startsWith("GOAL_")) {
      return (
        <CampaignStatusChip
          status={value as AssociatedCampaignStatus}
          sx={sx}
        />
      );
    }
    if (!isLineEntry(entry)) {
      return <BriefingStatusChip status={value as BriefingStatus} sx={sx} />;
    }
  }

  return (
    <Typography
      variant="body2"
      sx={{
        wordBreak: "break-word",
        color: previous ? "text.secondary" : "text.primary",
        ...(previous && !isEmptyHistoryValue(value)
          ? { textDecoration: "line-through" }
          : {}),
      }}
    >
      {formatHistoryValue(field, value, t, i18n?.language)}
    </Typography>
  );
};

const ChangeTable: React.FC<{
  entry: BriefingHistoryEntry;
  rows: Array<[string, BriefingHistoryChange]>;
}> = ({ entry, rows }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? rows : rows.slice(0, VISIBLE_ROWS);

  return (
    <Box sx={{ mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow
            sx={{ "& th": { fontWeight: 700, bgcolor: "grey.100", py: 0.5 } }}
          >
            <TableCell sx={{ width: "30%" }}>
              {t("briefingsView.history.colField", "Campo")}
            </TableCell>
            <TableCell sx={{ width: "35%" }}>
              {t("briefingsView.history.colBefore", "Antes")}
            </TableCell>
            <TableCell sx={{ width: "35%" }}>
              {t("briefingsView.history.colAfter", "Después")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.map(([field, change]) => (
            <TableRow key={field}>
              <TableCell sx={{ fontWeight: 600, verticalAlign: "top" }}>
                {historyFieldLabel(field, t)}
              </TableCell>
              <TableCell sx={{ verticalAlign: "top" }}>
                <ChangeValue
                  entry={entry}
                  field={field}
                  value={change.old}
                  previous
                />
              </TableCell>
              <TableCell sx={{ verticalAlign: "top" }}>
                <ChangeValue entry={entry} field={field} value={change.new} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {rows.length > VISIBLE_ROWS && (
        <Button size="small" onClick={() => setExpanded((v) => !v)}>
          {expanded
            ? t("briefingsView.history.showLess", "Ver menos")
            : t("briefingsView.history.showMore", "Ver {{count}} más", {
                count: rows.length - VISIBLE_ROWS,
              })}
        </Button>
      )}
    </Box>
  );
};

const HistoryEntryCard: React.FC<{ entry: BriefingHistoryEntry }> = ({
  entry,
}) => {
  const { t } = useTranslation();
  const systemLabel = t("briefingsView.history.system", "Sistema");
  const userName = historyUserName(entry.user, systemLabel);
  const rows = historyChangeRows(entry.changes).filter(
    ([field]) => field !== "automatic"
  );
  const automatic = historyChangeRows(entry.changes).some(
    ([field]) => field === "automatic"
  );

  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <Avatar sx={{ bgcolor: "primary.light", width: 36, height: 36 }}>
        {entry.user?.firstName?.[0] ||
          entry.user?.email?.[0]?.toUpperCase() || (
            <PersonIcon fontSize="small" />
          )}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="subtitle2" fontWeight="bold">
              {userName}
            </Typography>
            {entry.user?.email && userName !== entry.user.email && (
              <Typography variant="caption" color="text.secondary">
                {entry.user.email}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {formatDateTime(entry.createdAt)}
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          sx={{ mt: 0.75 }}
        >
          <Chip
            size="small"
            color={historyActionTone(entry.action)}
            label={historyActionLabel(entry.action, t)}
          />
          {isLineEntry(entry) && (
            <Chip
              size="small"
              variant="outlined"
              icon={<ViewListIcon />}
              label={
                entry.entityName ||
                t("briefingsView.history.unnamedLine", "Línea sin nombre")
              }
            />
          )}
          {automatic && (
            <Chip
              size="small"
              variant="outlined"
              icon={<AutorenewIcon />}
              label={t(
                "briefingsView.history.automatic",
                "Automático, a raíz de esta acción"
              )}
            />
          )}
        </Stack>

        {rows.length > 0 ? (
          <ChangeTable entry={entry} rows={rows} />
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            {t("briefingsView.history.noDetail", "Sin detalle de cambios")}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

/** Quién creó el briefing, quién creó la versión actual y quién lo tocó por última vez. */
const HistorySummary: React.FC<{ entries: BriefingHistoryEntry[] }> = ({
  entries,
}) => {
  const { t } = useTranslation();
  const systemLabel = t("briefingsView.history.system", "Sistema");
  const current = entries.filter((e) => !e.fromPreviousVersion);
  const origin = [...entries]
    .reverse()
    .find((e) => e.action === "CREATE" || e.action === "DUPLICATE");
  const versionCreated = current.find((e) => e.action === "VERSION_CREATE");
  const last = entries[0];

  const items = [
    origin && {
      label: t("briefingsView.history.createdBy", "Creado por"),
      entry: origin,
    },
    versionCreated && {
      label: t(
        "briefingsView.history.versionCreatedBy",
        "v{{version}} creada por",
        {
          version: versionCreated.briefingVersion,
        }
      ),
      entry: versionCreated,
    },
    last && {
      label: t("briefingsView.history.lastChangeBy", "Último cambio"),
      entry: last,
    },
  ].filter(Boolean) as Array<{ label: string; entry: BriefingHistoryEntry }>;

  if (!items.length) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1.5, sm: 4 }}
        useFlexGap
        flexWrap="wrap"
      >
        {items.map(({ label, entry }) => (
          <Box key={label}>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {historyUserName(entry.user, systemLabel)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(entry.createdAt)}
            </Typography>
          </Box>
        ))}
        <Box>
          <Typography variant="caption" color="text.secondary">
            {t("briefingsView.history.totalChanges", "Cambios registrados")}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {entries.length}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};

const BriefingHistoryTab: React.FC<BriefingHistoryTabProps> = ({
  briefingId,
}) => {
  const { t } = useTranslation();
  const {
    data: response,
    isLoading,
    isError,
  } = useGetBriefingHistoryQuery({ id: briefingId });

  const [scope, setScope] = useState<Scope>("all");
  const [line, setLine] = useState<string>(ALL);
  const [user, setUser] = useState<string>(ALL);
  const [includePrevious, setIncludePrevious] = useState(true);

  const entries = useMemo(() => response?.data ?? [], [response]);
  const hasPrevious = entries.some((e) => e.fromPreviousVersion);

  const lineOptions = useMemo(
    () =>
      Array.from(
        new Set(
          entries
            .filter(isLineEntry)
            .map((e) => e.entityName)
            .filter((name): name is string => Boolean(name))
        )
      ).sort((a, b) => a.localeCompare(b)),
    [entries]
  );

  const userOptions = useMemo(() => {
    const users = new Map<string, string>();
    entries.forEach((e) =>
      users.set(
        e.user?.email ?? SYSTEM_USER,
        historyUserName(e.user, t("briefingsView.history.system", "Sistema"))
      )
    );
    return Array.from(users.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [entries, t]);

  const filtered = useMemo(
    () =>
      entries.filter((e) => {
        if (!includePrevious && e.fromPreviousVersion) return false;
        if (scope === "briefing" && isLineEntry(e)) return false;
        if (scope === "lines" && !isLineEntry(e)) return false;
        if (line !== ALL && e.entityName !== line) return false;
        if (user !== ALL && (e.user?.email ?? SYSTEM_USER) !== user)
          return false;
        return true;
      }),
    [entries, includePrevious, scope, line, user]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        {t("briefingsView.history.fetchError", "Error al cargar el historial")}
      </Alert>
    );
  }

  if (entries.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <HistoryIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
        <Typography color="text.secondary">
          {t(
            "briefingsView.history.empty",
            "No hay historial disponible para este briefing"
          )}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <HistorySummary entries={entries} />

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
        useFlexGap
        flexWrap="wrap"
      >
        <ToggleButtonGroup
          size="small"
          exclusive
          value={scope}
          onChange={(_, value: Scope | null) => value && setScope(value)}
        >
          <ToggleButton value="all">
            {t("briefingsView.history.scopeAll", "Todo")}
          </ToggleButton>
          <ToggleButton value="briefing">
            {t("briefingsView.history.scopeBriefing", "Briefing")}
          </ToggleButton>
          <ToggleButton value="lines">
            {t("briefingsView.history.scopeLines", "Líneas")}
          </ToggleButton>
        </ToggleButtonGroup>

        {lineOptions.length > 0 && scope !== "briefing" && (
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="history-line-label">
              {t("briefingsView.history.line", "Línea")}
            </InputLabel>
            <Select
              labelId="history-line-label"
              label={t("briefingsView.history.line", "Línea")}
              value={line}
              onChange={(e) => setLine(e.target.value)}
            >
              <MenuItem value={ALL}>
                {t("briefingsView.history.allLines", "Todas las líneas")}
              </MenuItem>
              {lineOptions.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="history-user-label">
            {t("briefingsView.history.user", "Usuario")}
          </InputLabel>
          <Select
            labelId="history-user-label"
            label={t("briefingsView.history.user", "Usuario")}
            value={user}
            onChange={(e) => setUser(e.target.value)}
          >
            <MenuItem value={ALL}>
              {t("briefingsView.history.allUsers", "Todos")}
            </MenuItem>
            {userOptions.map(([key, name]) => (
              <MenuItem key={key} value={key}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {hasPrevious && (
          <FormControlLabel
            control={
              <Switch
                checked={includePrevious}
                onChange={(e) => setIncludePrevious(e.target.checked)}
              />
            }
            label={t(
              "briefingsView.history.includePrevious",
              "Incluir versiones anteriores"
            )}
          />
        )}
      </Stack>

      {filtered.length === 0 ? (
        <Typography color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
          {t(
            "briefingsView.history.noMatches",
            "Ningún cambio coincide con los filtros"
          )}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {filtered.map((entry, index) => {
            const previous = filtered[index - 1];
            const newVersion =
              hasPrevious &&
              (!previous || previous.briefingId !== entry.briefingId);
            return (
              <React.Fragment key={entry.id}>
                {newVersion && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      pt: index === 0 ? 0 : 1,
                    }}
                  >
                    <Chip
                      size="small"
                      color={entry.fromPreviousVersion ? "default" : "primary"}
                      label={`v${entry.briefingVersion}`}
                    />
                    <Typography variant="subtitle2" fontWeight={700}>
                      {entry.briefingName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {entry.fromPreviousVersion
                        ? t(
                            "briefingsView.history.previousVersion",
                            "Versión anterior"
                          )
                        : t(
                            "briefingsView.history.currentVersion",
                            "Esta versión"
                          )}
                    </Typography>
                  </Box>
                )}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    ...(entry.fromPreviousVersion
                      ? { bgcolor: "grey.50" }
                      : {}),
                  }}
                >
                  <HistoryEntryCard entry={entry} />
                </Paper>
              </React.Fragment>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export default BriefingHistoryTab;
